import {colors} from 'shells/colors/colors';
import {NavIconDashboardIcon} from 'shells/icon';
import {NavIconAutomationIcon} from 'shells/icon/navigation/nav-icon-automation';
import {BoxRegularIcon} from 'shells/icon/svg/box-regular-icon';
import {CalendarRegularPenIcon} from 'shells/icon/svg/calendar-regular-pen-icon';
import {EnvelopeOpenRegularTemplateIcon} from 'shells/icon/svg/envelope-open-regular-template-icon';
import {InvoiceRegularIcon} from 'shells/icon/svg/invoice-regular-icon';
import {PersonRegularSearchIcon} from 'shells/icon/svg/person-regular-search-icon';
import {PhoneOutlineIcon} from 'shells/icon/svg/phone-outline-icon';
import {QuoteRegularIcon} from 'shells/icon/svg/quote-regular-icon';
import {TargetRegularIcon} from 'shells/icon/svg/target-regular-icon';
import {VideoCameraRegularIcon} from 'shells/icon/svg/video-camera-regular-icon';
import {SecondarySidebar} from 'shells/navigation/secondary-sidebar/secondary-sidebar';
import type {NavItem, SubmenuNavItem} from 'shells/navigation/types';

import {useGetInstanceInfo} from '../core/use-get-instance-info';

import {SidebarFooter} from './sidebar-footer';

const getSalesNavItems = (
    canAccessSetup: boolean,
    canUseProspectorIq: boolean,
    canUseSalesDocuments: boolean,
    hasSchedulerFullAccess: boolean
): NavItem[] => {
    const navItems: NavItem[] = [];

    navItems.push({
        label: 'Dashboard',
        // @ts-expect-error
        icon: () => <NavIconDashboardIcon size={14} color={colors.white} />,
        to: '/sales/dashboard',
    });

    navItems.push({
        label: 'Outreach',
        isHeader: true,
    });

    if (!canAccessSetup) {
        navItems.push({
            label: 'Scheduler',
            // @ts-expect-error
            icon: () => <CalendarRegularPenIcon size={16} color={colors.white} />,
            to: '/sales/scheduler',
        });
    } else {
        const schedulerSubmenu = [
            {
                label: 'Overview',
                to: '/sales/scheduler',
                activeMatch: /^\/sales\/scheduler(?!\/managed)/,
            },
            {
                label: 'Managed meetings',
                to: '/sales/scheduler/managed',
                requiresUpgrade: !hasSchedulerFullAccess,
            },
        ];

        navItems.push({
            label: 'Scheduler',
            // @ts-expect-error
            icon: () => <CalendarRegularPenIcon size={16} color={colors.white} />,
            to: '/sales/scheduler',
            submenu: schedulerSubmenu,
        });
    }

    navItems.push({
        label: 'Calls',
        // @ts-expect-error
        icon: () => <PhoneOutlineIcon size={16} color={colors.white} />,
        to: '/sales/calls',
    });

    const meetingsSubmenu: SubmenuNavItem[] = [
        {
            label: 'Zoom',
            to: '/sales/meetings/zoom',
            requiresUpgrade: !NutClientConfig.hasZoomTranscription,
        },
    ];

    meetingsSubmenu.push({
        label: 'Google Meet',
        to: '/sales/meetings/meet',
        requiresUpgrade: !NutClientConfig.hasGoogleMeetTranscription,
    });

    if (NutClientConfig.hasMicrosoftTeamsFF) {
        meetingsSubmenu.push({
            label: 'Microsoft Teams',
            to: '/sales/meetings/teams',
        });
    }

    navItems.push({
        label: 'Meetings',
        // @ts-expect-error
        icon: () => <VideoCameraRegularIcon size={16} color={colors.white} />,
        to: '/sales/meetings',
        submenu: meetingsSubmenu,
    });

    if (canUseProspectorIq) {
        navItems.push({
            label: 'ProspectorIQ',
            // @ts-expect-error
            icon: () => <PersonRegularSearchIcon size={16} color={colors.white} />,
            to: '/sales/prospectoriq',
        });
    }

    navItems.push({
        label: 'Email templates',
        // @ts-expect-error
        icon: () => <EnvelopeOpenRegularTemplateIcon size={16} color={colors.white} />,
        to: '/sales/email/templates',
        isLegacyIcon: false,
    });

    // To keep current sidebar ordering without gating Quotes behind canAccessSetup
    const shouldPushAutomationAndQuotas = canAccessSetup;

    const quoteSubmenu = [];
    const invoicesSubmenu = [];

    if (canUseSalesDocuments) {
        navItems.push({
            label: 'Revenue',
            isHeader: true,
        });

        quoteSubmenu.push({
            label: 'Overview',
            to: '/sales/quote-overview',
        });

        if (canAccessSetup) {
            quoteSubmenu.push({
                label: 'Template',
                to: '/sales/quote-template',
            });
        }

        if (!NutClientConfig.hasQuotePlan) {
            quoteSubmenu.push({
                label: 'Upgrade',
                to: '/sales/quote-upgrade',
                requiresUpgrade: true,
            });
        }

        navItems.push({
            label: 'Quotes',
            // @ts-expect-error
            icon: () => <QuoteRegularIcon size={16} color={colors.white} />,
            to: '/sales/quote',
            submenu: quoteSubmenu,
        });

        invoicesSubmenu.push({
            label: 'Overview',
            to: '/sales/invoice-overview',
        });

        if (canAccessSetup) {
            invoicesSubmenu.push({
                label: 'Template',
                to: '/sales/invoice-template',
            });
        }

        if (!NutClientConfig.hasQuotePlan) {
            invoicesSubmenu.push({
                label: 'Upgrade',
                to: '/sales/invoice-upgrade',
                requiresUpgrade: true,
            });
        }

        navItems.push({
            label: 'Invoices',
            // @ts-expect-error
            icon: () => <InvoiceRegularIcon size={16} color={colors.white} />,
            to: '/sales/invoice',
            submenu: invoicesSubmenu,
        });

        if (canAccessSetup) {
            navItems.push({
                label: 'Products',
                // @ts-expect-error
                icon: () => <BoxRegularIcon size={16} color={colors.white} />,
                to: '/sales/product',
                submenu: [
                    {
                        id: 'products',
                        label: 'Overview',
                        to: '/sales/product',
                        activeMatch: /^\/sales\/product$/,
                    },
                    {
                        id: 'categories',
                        label: 'Categories',
                        to: '/sales/product/categories',
                    },
                ],
            });
        }
    }

    if (shouldPushAutomationAndQuotas) {
        navItems.push({
            label: 'Strategy',
            isHeader: true,
        });

        navItems.push({
            label: 'Pipelines',
            icon: NavIconAutomationIcon,
            to: '/sales/automation',
            submenu: [
                {
                    label: 'Overview',
                    to: '/sales/pipelines',
                },
                {
                    label: 'Lead outcomes',
                    to: '/sales/lead-outcomes',
                },
            ],
        });

        navItems.push({
            label: 'Quotas',
            // @ts-expect-error
            icon: () => <TargetRegularIcon size={16} color={colors.white} />,
            to: '/sales/sales-quotas',
            submenu: [
                {
                    label: 'Sales quotas',
                    to: '/sales/sales-quotas',
                },
                {
                    label: 'Activity quotas',
                    to: '/sales/activity-quotas',
                },
            ],
        });
    }

    return navItems;
};

type Props = {
    canAccessSetup: boolean;
    canUseProspectorIq: boolean;
    canUseSalesDocuments: boolean;
};

export function SalesSidebar(props: Props) {
    const {sessionInstance} = useGetInstanceInfo();

    return (
        <SecondarySidebar
            navItems={getSalesNavItems(
                props.canAccessSetup,
                props.canUseProspectorIq,
                props.canUseSalesDocuments,
                Boolean(sessionInstance && sessionInstance.schedulerAccessType === 'FULL_ACCESS')
            )}
            footer={<SidebarFooter />}
        />
    );
}
